// apps/admin/src/features/settings/ConflictDiffModal.tsx
//
// Phase 8.3 Wave 6 Task 41 — VERSION_CONFLICT (HTTP 409) diff modal.
//
// vg-binding: CONTEXT:decisions=P8.D-101,P8.D-120,P8.D-170
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per task-41 PLAN ("On 409 VERSION_CONFLICT → DiffViewer modal 'Server has
// newer version' + Re-load / Force Overwrite (SA only audit-logged)") and the
// PATCH /api/v1/admin/settings/:namespace contract — `error.details` carries
// `current_version`, `current_values`, `expected_version` so we can render
// the diff without an extra GET round-trip.
//
// UX:
//   - Title:  "Server has newer version"
//   - Body:   3-column diff table (Path | Your Value | Server Value)
//   - Footer: [Re-load] (reload server values into form, primary) +
//             [Force Overwrite] (resend PATCH with current_version, SA-only).
//   - "Force Overwrite" is HIDDEN for non-SA — the BE audit log will record
//     the action either way, but the UI affordance is gated to
//     superadmin per task brief.
//
// Accessibility:
//   - Inherits focus trap + ESC handling from <Dialog>.
//   - aria-describedby points at the body explainer paragraph.

import { useMemo, type JSX } from 'react';

import {
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

export interface ConflictDiffModalProps {
  /** Show / hide the modal. */
  open: boolean;
  /** Caller's pending edits (the values they tried to save). */
  yourValues: Record<string, unknown>;
  /** Server's current values (from `error.details.current_values`). */
  serverValues: Record<string, unknown>;
  /** Server's current version (from `error.details.current_version`). */
  currentVersion: number;
  /** Version the caller's PATCH was attempting to bump from. */
  expectedVersion: number;
  /** Whether the active user holds the superadmin role (Force Overwrite gate). */
  isSuperAdmin: boolean;
  /** Re-load button — caller re-pulls server values into form state. */
  onReload: () => void;
  /** Force overwrite — caller re-PATCHes with `current_version` echoed. */
  onForceOverwrite: () => void;
  /** ESC / backdrop close — same UX as Re-load (safe default). */
  onClose: () => void;
}

interface DiffRow {
  path: string;
  yours: unknown;
  server: unknown;
}

function computeDiff(
  yours: Record<string, unknown>,
  server: Record<string, unknown>,
): DiffRow[] {
  const keys = new Set<string>([...Object.keys(yours), ...Object.keys(server)]);
  const rows: DiffRow[] = [];
  for (const key of keys) {
    const a = yours[key];
    const b = server[key];
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    rows.push({ path: key, yours: a, server: b });
  }
  return rows.sort((x, y) => x.path.localeCompare(y.path));
}

function renderValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 0);
  } catch {
    return String(value);
  }
}

/**
 * `<ConflictDiffModal>` — render-only modal triggered when PATCH returns
 * `code='VERSION_CONFLICT'`. Computes the diff client-side from the two
 * value maps (yours vs server) and surfaces a 3-column table.
 */
export function ConflictDiffModal({
  open,
  yourValues,
  serverValues,
  currentVersion,
  expectedVersion,
  isSuperAdmin,
  onReload,
  onForceOverwrite,
  onClose,
}: ConflictDiffModalProps): JSX.Element | null {
  const { t } = useTranslation('settings');
  const diff = useMemo(
    () => computeDiff(yourValues, serverValues),
    [yourValues, serverValues],
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-3xl"
      aria-label={t('conflictDiff.aria')}
      data-testid="settings-conflict-modal"
    >
      <DialogHeader>
        <DialogTitle>{t('conflictDiff.title')}</DialogTitle>
        <DialogDescription id="conflict-diff-modal-body">
          {t('conflictDiff.description', {
            currentVersion,
            expectedVersion,
          })}
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[50vh] overflow-y-auto px-1 py-3">
        {diff.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-pw-text-muted">
            {t('conflictDiff.noDiffs')}
          </p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="sticky top-0 bg-pw-surface text-xs font-medium uppercase tracking-wide text-pw-text-muted">
              <tr>
                <th className="w-1/4 px-3 py-2">{t('conflictDiff.columns.field')}</th>
                <th className="w-3/8 px-3 py-2">{t('conflictDiff.columns.yourValue')}</th>
                <th className="w-3/8 px-3 py-2">{t('conflictDiff.columns.serverValue')}</th>
              </tr>
            </thead>
            <tbody data-testid="settings-conflict-diff-rows">
              {diff.map((row) => (
                <tr
                  key={row.path}
                  className="border-t border-pw-border align-top"
                >
                  <td className="px-3 py-2 font-mono text-xs text-pw-text">
                    {row.path}
                  </td>
                  <td className="px-3 py-2 text-pw-text">
                    <code className="break-all whitespace-pre-wrap rounded bg-pw-primary/5 px-1.5 py-0.5 text-xs">
                      {renderValue(row.yours)}
                    </code>
                  </td>
                  <td className="px-3 py-2 text-pw-text">
                    <code className="break-all whitespace-pre-wrap rounded bg-pw-surface px-1.5 py-0.5 text-xs">
                      {renderValue(row.server)}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="default"
          onClick={onReload}
          data-testid="settings-conflict-reload-btn"
        >
          {t('conflictDiff.reload')}
        </Button>
        {isSuperAdmin ? (
          <Button
            type="button"
            variant="destructive"
            onClick={onForceOverwrite}
            data-testid="settings-conflict-force-btn"
          >
            {t('conflictDiff.forceOverwrite')}
          </Button>
        ) : null}
      </DialogFooter>
    </Dialog>
  );
}

export default ConflictDiffModal;
